import { DiscountType } from '@/enums/DiscountType';
import { IDiscount } from '@/interfaces/IDiscount';
import { IOrder } from '@/interfaces/IOrder';
import { displayPrice, roundPrice } from '@/utils';

/**
 * Pure resolver for the printable discount lines of an order. Extracted
 * verbatim from the single-ticket totals block so the group document can
 * reuse the exact same semantics (labels, percentage caps, legacy fallback).
 *
 * - `label` values EXCLUDE the trailing colon; consumers print
 *   `encoder.keyValue(`${label}:`, valueDisplay)`.
 * - Gate semantics (CRITICAL, mirrors the historical inline block): the outer
 *   gate is `order.discounts` TRUTHINESS. An EMPTY discounts array yields zero
 *   lines and `totalDiscountAmount = 0` — it must NOT fall back to the legacy
 *   `order.discount` field. Legacy applies ONLY when `order.discounts` is
 *   null/undefined.
 * - Legacy path returns `totalDiscountAmount = order.discountAmount` so the
 *   group SUBTOTAL arithmetic holds on legacy orders.
 */
export interface IDiscountLine {
  label: string;
  valueDisplay: string;
}

export interface IOrderDiscountLinesResult {
  lines: IDiscountLine[];
  totalDiscountAmount: number;
}

export function computeOrderDiscountLines(
  order: IOrder,
  currencySymbol: string
): IOrderDiscountLinesResult {
  if (order.discounts) {
    // New discount system - show individual discounts
    const discounts: IDiscount[] = order.discounts;
    const lines: IDiscountLine[] = [];
    let totalDiscountAmount = 0;

    if (discounts && discounts.length > 0) {
      for (const discount of discounts) {
        let discountLabel: string;
        let calculatedAmount: number = 0;

        // Generate label based on discount type
        switch (discount.type) {
          case DiscountType.Manual:
            discountLabel = 'DESCUENTO';
            break;
          case DiscountType.Coupon: {
            const couponCode = discount.metadata?.couponCode || '';
            discountLabel = couponCode ? `CUPÓN ${couponCode}` : 'CUPÓN';
            break;
          }
          case DiscountType.Rule: {
            const ruleName = discount.metadata?.ruleName || '';
            discountLabel = ruleName ? `PROMOCIÓN ${ruleName}` : 'PROMOCIÓN';
            break;
          }
          default:
            discountLabel = 'DESCUENTO';
        }

        // Calculate the discount amount
        if (discount.isPercentage) {
          // Percentage discounts apply only to products (subtotal)
          calculatedAmount = roundPrice(
            order.subtotal * (discount.value / 100)
          );

          if (typeof discount.metadata?.maxDiscountAmount === 'number') {
            calculatedAmount = roundPrice(
              Math.min(calculatedAmount, discount.metadata.maxDiscountAmount)
            );
          }
        } else {
          // Fixed amount discount
          calculatedAmount = discount.value;
        }

        totalDiscountAmount += calculatedAmount;

        let valueDisplay: string;
        if (discount.isPercentage) {
          valueDisplay = `-%${discount.value} (${currencySymbol}${displayPrice(
            calculatedAmount,
            true
          )})`;
        } else {
          valueDisplay = `(${currencySymbol}${displayPrice(
            calculatedAmount,
            true
          )})`;
        }

        lines.push({ label: discountLabel, valueDisplay });
      }
    }

    return { lines, totalDiscountAmount };
  }

  if (order.discount) {
    // Legacy discount system
    return {
      lines: [
        {
          label: `DESCUENTO (%${order.discount})`,
          valueDisplay: `${currencySymbol}${displayPrice(
            order.discountAmount,
            true
          )}`,
        },
      ],
      totalDiscountAmount: order.discountAmount ?? 0,
    };
  }

  return { lines: [], totalDiscountAmount: 0 };
}
